import React, {
  useCallback,
  useMemo,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import AppContext from "../Store/AppContext";
import { getUrl } from "../Network/urls";
import {
  PaginationModule,
  ContextMenuModule,
  ServerSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  ValidationModule,
  ColumnApiModule,
  ScrollApiModule,
  ColumnMenuModule,
  SetFilterModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  IntegratedChartsModule,
  PivotModule,
  RowGroupingPanelModule,
  ColumnAutoSizeModule,
} from "ag-grid-enterprise";
import { CsvExportModule } from "ag-grid-community";
import NotSubscribedMessageComponent from "./UI/NotSubscribedMessageComponent";
import CircularProgress from "@mui/material/CircularProgress";
import "../Assets/css/euRecordKeepingAnalytics.css";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import { get } from "../Network/requests";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import { TextField } from "@mui/material";
import { Slider, Box } from "@mui/material";
import { TbArrowsMinimize } from "react-icons/tb";
import { LuExpand } from "react-icons/lu";
import GridRowDetailsExpand from "./UI/GridRowDetailsExpand";
import GridGroupIcons from "./UI/GridGroupIcons";
import CustomDatepicker from "./UI/CustomDatepicker";
import { tokenExpire } from "../Utils";
import { useNavigate } from "react-router-dom";
import GridDataNotFound from "./UI/GridDataNotFound";
import DynamicTabSwitcher from "./UI/DynamicTabSwitcher";
import { BasicKPICalculation } from "./UI/KpiCalculationModal";
import { FaInfoCircle } from "react-icons/fa";

// Register AG Grid Enterprise Modules
ModuleRegistry.registerModules([
  PaginationModule,
  ContextMenuModule,
  ServerSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  ValidationModule,
  CsvExportModule,
  ColumnApiModule,
  ScrollApiModule,
  ColumnMenuModule,
  SetFilterModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule),
  PivotModule,
  RowGroupingPanelModule,
  ColumnAutoSizeModule,
]);

function UKRecordKeepingAnnexIVExecutionsComponent({
  productCode,
  regulationCode,
  isActive,
  dateRange,
  setDateRange,
}) {
  // console.log("executions :", regulationCode);
  // Get client details from context
  const {
    clientDetails,
    handleAPIEncryptValue,
    handleDecryptResponse,
    handleEncryptValue,
  } = useContext(AppContext);
  const gridRef = useRef();
  const navigate = useNavigate();
  const [isRegulationAccessible, setIsRegulationAccessible] = useState(false);
  const [gridApi, setGridApi] = useState(null);

  const [startDate, endDate] = dateRange;
  // Ref to hold the debounce timer for filter refresh
  const filterChangedTimeout = useRef(null);
  // Ref to store previous active state for asynchronous filters (first 4 columns)
  const prevAsyncActiveState = useRef({});
  // console.log("udpated start date :", startDate);
  // console.log("udpated end Date :", endDate);
  // URL endpoints from your configuration
  const filterUrl = getUrl("get-falsk-filter-values");
  const downloadUrl = getUrl("get-download-csv");
  const [isDownloading, setIsDownloading] = useState(false); // <-- Download state
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [showContactSupport, setShowContactSupport] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [rowRange, setRowRange] = useState([100000, 500000]);
  const [rowRangeLimits, setRowRangeLimits] = useState({
    min: 100000,
    max: 500000,
  });

  // KPI States
  const emptyKpis = {
    totalExe: 0,
    validExe: 0,
    validPctOfTotal: 0,
    invalidExe: 0,
    invalidPctOfTotal: 0,
    excludedExe: 0,
    excludedPctOfTotal: 0,
    accuracyPct: 0,
    errorPct: 0,
  };
  const [kpiData, setKpiData] = useState(emptyKpis);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [activeBasicKpiKey, setActiveBasicKpiKey] = useState(null);

  const openBasicKpiModal = (key) => setActiveBasicKpiKey(key);
  const closeBasicKpiModal = () => setActiveBasicKpiKey(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [selectedRow, setSelectedRow] = useState({});
  const [isDetailBoxOpen, setIsDetailBoxOpen] = useState(false);
  const [hoverPos, setHoverPos] = useState(null); // {x, y}
  const [showDialog, setShowDialog] = useState(false);
  const timerRef = useRef("");

  const TAB_MAP = {
    rcd: "Records",
    err: "Errors",
  };
  const [activeTab, setActiveTab] = useState(Object.keys(TAB_MAP)[0]);
  const activeTabRef = useRef(Object.keys(TAB_MAP)[0]);

  const handleTabChange = (tab) => {
    // console.log("tab :", tab);
    activeTabRef.current = tab;
    setActiveTab(tab);
  };

  // Refresh grid when activeTab changes
  useEffect(() => {
    if (gridApi && activeTab) {
      gridApi.refreshServerSide({ purge: true });
    }
  }, [activeTab, gridApi]);

  // Helper functions for KPI calculations
  const fmt = (num) => Number(num).toLocaleString("en-US");
  const getRowByType = (rows, type) => rows.find((r) => r?.[0] === type) ?? [];
  const getValue = (row, idx) => Number(row?.[idx] ?? 0);

  // Fetch KPI data
  const fetchKpiData = async () => {
    if (!startDate || !endDate) return;

    setKpiLoading(true);
    try {
      const start = startDate.toLocaleDateString("fr-CA");
      const end = endDate ? new Date(new Date(endDate).setDate(endDate.getDate() + 1)).toLocaleDateString("fr-CA") : "";
      const encryptedOperation = handleEncryptValue("grid_kpi");
      const encryptedRole = handleEncryptValue(clientDetails.role);
      const encryptedClientId = handleEncryptValue(clientDetails.clientId);
      const encryptedProductCode = handleEncryptValue(productCode);
      const encryptedRegulation = handleEncryptValue(regulationCode);
      const encryptedStartDate = handleEncryptValue(start);
      const encryptedEndDate = handleEncryptValue(end);

      const data = {
        client_id: encryptedClientId,
        role: encryptedRole,
        product: encryptedProductCode,
        regulation: encryptedRegulation,
        email_id: clientDetails.email,
        start_date: encryptedStartDate,
        end_date: encryptedEndDate,
        operations: encryptedOperation,
      };

      const regdataData = handleAPIEncryptValue(JSON.stringify(data));
      const url = getUrl("get-dashboard");

      const response = await get(`${url}?regdata=${regdataData}`, true);
      const { data: responseData } = response;

      if (responseData.statusCode === 200) {
        const iv = responseData.authData;
        const idToken = localStorage.getItem("userToken");
        const secretKey = idToken.slice(998, 1030);
        const body = handleDecryptResponse(
          responseData.encryptBody.toString(),
          secretKey.toString(),
          iv.toString(),
        );

        const kpiSource = body?.regdata_kpi_data?.P004;
        const rows = kpiSource?.data ?? [];
        const columns = kpiSource?.columns ?? [];

        if (!rows.length || !columns.length) {
          setKpiData(emptyKpis);
          return;
        }

        const validIdx = columns.indexOf("Valid Record");
        const invalidIdx = columns.indexOf("Invalid Record");
        const excludedIdx = columns.indexOf("Excluded Records");

        const executionRow = getRowByType(rows, "Execution");

        const validExe = getValue(executionRow, validIdx);
        const invalidExe = getValue(executionRow, invalidIdx);
        const excludedExe = getValue(executionRow, excludedIdx);

        const totalExe = validExe + invalidExe + excludedExe;
        const processable = validExe + invalidExe;

        const validPctOfTotal = totalExe ? (validExe / totalExe) * 100 : 0;
        const invalidPctOfTotal = totalExe ? (invalidExe / totalExe) * 100 : 0;
        const excludedPctOfTotal = totalExe
          ? (excludedExe / totalExe) * 100
          : 0;

        const accuracyPct = processable ? (validExe / processable) * 100 : 0;
        const errorPct = processable ? (invalidExe / processable) * 100 : 0;

        setKpiData({
          totalExe,
          validExe,
          validPctOfTotal,
          invalidExe,
          invalidPctOfTotal,
          excludedExe,
          excludedPctOfTotal,
          accuracyPct,
          errorPct,
        });
      }
    } catch (error) {
      console.error("Error fetching KPI data:", error);
      setKpiData(emptyKpis);
    } finally {
      setKpiLoading(false);
    }
  };

  // KPI Value wrapper component
  const KpiValue = ({ children, className = "text-start" }) => {
    if (kpiLoading) {
      return (
        <div className={className}>
          <CircularProgress className={className} size={16} />
        </div>
      );
    }
    return <>{children}</>;
  };
  // const handleDateRangeChanged = (dates) => {
  // const [start, end] = dates;
  // console.log("dates :", dates);

  // if (start && end) {
  //   const diffTime = Math.abs(end - start);
  //   const diffDays = diffTime / (1000 * 60 * 60 * 24);

  //   if (diffDays > 7) {
  //     toast.error("Maximum 7 days allowed");
  //     return;
  //   }
  // }

  // Update state only if valid
  // setStartDate(start);
  // setEndDate(end);
  // };

  const onCellMouseOver = (params) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const e = params.event;
      setHoverPos({ x: e.clientX, y: e.clientY });
      setShowDialog(true);
    }, 200);
  };

  const onCellMouseOut = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setShowDialog(false);
  };
  const handleDoubleClick = (params) => {
    setIsDetailBoxOpen(true);
    const clickedRow = params.data;
    if (clickedRow && Object.keys(clickedRow).length) {
      const isFieldIncludeUnderscore = Object.keys(clickedRow).some((field) =>
        field.includes("_"),
      );
      if (isFieldIncludeUnderscore) {
        let formattedData = {};
        params.api.getColumnDefs().forEach((col) => {
          formattedData[col.headerName] = clickedRow[col.field];
        });
        setSelectedRow(formattedData);
      } else {
        setSelectedRow(clickedRow);
      }
    }
  };
  const gridKey = useMemo(() => {
    const sd = startDate?.toISOString().slice(0, 10) ?? "";
    const ed = endDate?.toISOString().slice(0, 10) ?? "";
    // console.log(sd);
    // console.log(ed);
    return `${sd}_${ed}_${activeTab}_${refreshCounter}`;
  }, [startDate, endDate, refreshCounter, activeTab]);

  const handleRefresh = useCallback(() => {
    // force a full unmount/remount:
    setRefreshCounter((count) => count + 1);
  }, [gridApi]);

  // 1) Small util
  const toYMD = (val) => {
    if (!val) return val;
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val; // if it's already a string, leave it
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  // --------------------------------------------------------------------------
  // 1. Asynchronous filter values provider
  // --------------------------------------------------------------------------
  const getAsyncFilterValues = (params) => {
    const filterModel = (gridApi && gridApi.getFilterModel()) || {};
    // Create a shallow copy and remove the current field.
    const activeFilters = { ...filterModel };
    const currentField = params.colDef.field;
    delete activeFilters[currentField];
    const s = startDate?.toLocaleDateString("fr-CA") ?? "";
    const e = endDate ? new Date(new Date(endDate).setDate(endDate.getDate() + 1)).toLocaleDateString("fr-CA") : "";
    const queryParams = new URLSearchParams({
      client_id: clientDetails.clientId,
      product: productCode,
      regulation: regulationCode,
      filters: JSON.stringify(activeFilters),
      email: clientDetails.email,
      datefiltercolumn: "order_date_regx",
      ...(s && { start_date: s }),
      ...(e && { end_date: e }),
      f_type: activeTabRef.current, // err || records
    });
    // for (const [key, value] of queryParams.entries()) {
    //   console.log(`${key}: ${value}`);
    // }
    const regDataGridData = handleAPIEncryptValue(queryParams.toString());
    const token = clientDetails.IdToken;
    fetch(`${filterUrl}?regdata=${regDataGridData}`, {
      method: "GET",
      mode: "cors",
      headers: {
        "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        // Expect data to be an object mapping field names to arrays.
        params.success(data[currentField] || []);
      })
      .catch((error) => {
        console.error(
          `Error fetching filter values for ${currentField}:`,
          error,
        );
        params.success([]);
      });
  };

  // Formatter function for the Creation Date column
  const dateFormatter = (params) => {
    if (!params.value) return "";
    const date = new Date(params.value);
    return date.toISOString().split("T")[0]; // Returns YYYY-MM-DD
  };

  const handleClearFilters = useCallback(() => {
    if (!gridApi) return;

    // clear all column filters
    gridApi.setFilterModel(null);

    // clear sorting (optional)
    gridApi.setSortModel?.(null);

    // clear quick filter if you ever use it (safe to keep)
    gridApi.setGridOption?.("quickFilterText", "");
    gridApi.setQuickFilter?.("");

    // refresh SSRM
    gridApi.refreshServerSide({ purge: true });

    toast.success("All filters cleared");
  }, [gridApi]);

  const runAutosize = useCallback(() => {
    if (!gridApi) return;

    // ensures grid calculates with correct width
    gridApi.doLayout?.();

    requestAnimationFrame(() => {
      try {
        const colIds = gridApi
          .getAllDisplayedColumns()
          .map((c) => c.getColId());
        gridApi.autoSizeColumns(colIds, false);
      } catch (e) {
        gridApi.autoSizeAllColumns?.();
      }
    });
  }, [gridApi]);

  useEffect(() => {
    if (!isActive) return;

    // small delay helps because tab switches use display:none -> block
    const t = setTimeout(() => {
      runAutosize();
    }, 80);

    return () => clearTimeout(t);
  }, [isActive, runAutosize]);

  // --------------------------------------------------------------------------
  // 2. Column definitions
  // --------------------------------------------------------------------------
  const defaultColDef = useMemo(
    () => ({
      // flex: 1,
      resizable: true,
      sortable: true,
      cellStyle: { textAlign: "left" },
      floatingFilter: false,
      suppressMenu: false, // we need the column menu open for pivot
      enableValue: true,
      enablePivot: true,
      enableRowGroup: true, // drag here to pivot
    }),
    [],
  );

  const recordsColDefs = useMemo(
    () => [
      {
        field: "record_type_regx",
        headerName: "Trade Type",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "source_system_regx",
        headerName: "Source System",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "cfi_code_regx",
        headerName: "CFI Code",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "product_regx",
        headerName: "Product",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_date_regx",
        headerName: "Trade Date",
        filter: "agSetColumnFilter",
        valueFormatter: (params) => {
          const raw = params.value;
          if (!raw) return "";
          const d = new Date(raw);
          if (isNaN(d)) return raw;
          // console.log("raw  :", d.toISOString().split("T")[0]);
          return d.toISOString().split("T")[0];
        },
        filterParams: {
          values: getAsyncFilterValues,
          keyCreator: (p) => toYMD(p.value),
          valueFormatter: (p) => toYMD(p.value),
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
          treeList: true,
          treeListPathGetter: (rawDateValue) => {
            // console.log("rawDateValue :", rawDateValue);
            if (!rawDateValue) return ["Unknown"];
            const d = new Date(rawDateValue);
            if (isNaN(d)) return [rawDateValue];
            // console.log("d  :", d?.toLocaleDateString("fr-CA"));
            const year = String(d.getFullYear());
            const monthName = d.toLocaleString("en-US", { month: "short" });
            const day = String(d.getDate()).padStart(2, "0");
            // return d?.toLocaleDateString("fr-CA");
            return [year, monthName, day];
          },
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "name_and_deg_of_client",
        headerName: "Name and designation of the client",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "name_of_desig_of_the_client",
        headerName:
          "Name and designation of any relevant person acting on behalf of the client",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trader_id",
        headerName:
          "A designation to identify the trader (Trader ID) responsible within the investment firm for the investment decision",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "algorithm_id",
        headerName:
          "A designation to identify the Algo (Algo ID) responsible within the investment firm for the investment decision",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "txnr_ref_number",
        headerName: "Transaction reference number",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_id",
        headerName: "A designation to identify the order (Order ID)",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "idf_code_of_ord",
        headerName:
          "The identification code of the order assigned by the trading venue upon receipt of the order",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "unq_id_of_each_aggr_clients",
        headerName:
          "A unique identification for each group of aggregated clients' orders (which will be subsequently placed as one block order on a given trading venue).This identification should indicated ‘aggregated_X’ with X representing the number of clients whose orders have been aggregated",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "mic_code",
        headerName:
          "The segment MIC code of the trading venue to which the order has been submitted",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "name_of_odr_transmitted",
        headerName:
          "The name and other designation of the person to whom the order was transmitted",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "desg_of_seller_buyer",
        headerName: "Designation to identify the Seller & the Buyer",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trading_capacity",
        headerName: "The trading capacity",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "execution_trader_id",
        headerName:
          "A designation to identify the Trader (Trader ID) responsible for the execution",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "execution_algo_id",
        headerName:
          "A designation to identify the Algo (Algo ID) responsible for the execution",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "bs_indicator",
        headerName: "B/S indicator",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "date_and_exact_time_of_recipt",
        headerName:
          "The date and exact time of the receipt of the order or the dathe and exact time of when the decision to deal was made.  The exact time must be measured according to the methodology prescribed under the standards on clock synchronisation under Article 50(2) of Directive 2014/65/EU",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "ultimate_underlying",
        headerName: "Ultimate Underlying",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "put_call_indicator",
        headerName: "Put/Call identifier",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "strike_price",
        headerName: "Strike price",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "up_front_paymet",
        headerName: "Up-front payment",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "delivery_type",
        headerName: "Delivery type",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "option_style",
        headerName: "Option style",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "maturity_date",
        headerName: "Maturity date",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "unit_price_and_price_notation",
        headerName: "Unit price and price notation",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "price",
        headerName: "Price",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "price_multiplyer",
        headerName: "Price Multiplier",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "currency1",
        headerName: "Currency 1",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "currency2",
        headerName: "Currency 2",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "remaining_quantity",
        headerName: "Remaining Quantity",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "modified_quantity",
        headerName: "Modified Quantity",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "executed_quantity",
        headerName: "Executed Quantity",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_submission_datetime",
        headerName:
          "The date and exact time of submission of the order or decision to deal. The exact time must be measured according to the methodology prescribed under the standards on clock synchronisation under Article 50(2) of Directive 2014/65/EU",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trading_venue_affecting_datetime",
        headerName:
          "The date and exact time of any message that is transmitted to and received from the trading venue in relation to any events affecting an order",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "another_invst_firm_affecting_datetime",
        headerName:
          "The date and exact time any message that is transmitted to and received from another investment firm in relation to any events affecting an order  The exact time must be measured according to the methodology prescribed under Commission Delegathed Regulation (EU) 2017/574 (1).  The exact time must be measured according to the methodology prescribed under the standards on clock synchronisation under Article 50(2) of Directive 2014/65/EU",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "message_from_trading_venue",
        headerName:
          "Any message that is transmitted to and received from the trading venue in relation to orders placed by the investment firm",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "details_recived_from_anothr_invst_firm",
        headerName:
          "Any other details and conditions that was submitted to and received from another investment firm in relation with the order",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "placed_order_sequence",
        headerName:
          "Each placed order's sequences in order to reflect the chronology of every event affecting it, including but not limited to modifications, cancellations and execution",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "short_selling_flag",
        headerName: "Short selling flag",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "ssr_ecemption_flag",
        headerName: "SSR exemption flag",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "waiver_flag",
        headerName: "Waiver flag",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
    ],
    [clientDetails, productCode, regulationCode, gridApi],
  );

  const errorColDefs = useMemo(
    () => [
      {
        field: "record_type_regx",
        headerName: "Trade Type",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "source_system_regx",
        headerName: "Source System",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "cfi_code_regx",
        headerName: "CFI Code",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "product_regx",
        headerName: "Product",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      // added on 23-Jan
      {
        field: "error_description",
        headerName: "Error Details",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
        },
        width: 300,
        minWidth: 300,
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "input_file_column",
        headerName: "Input File Column",
        filter: "agSetColumnFilter",
        filterParams: {
          values: getAsyncFilterValues,
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
        },
        width: 300,
        minWidth: 300,
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      // --
      {
        field: "order_date_regx",
        headerName: "Trade Date",
        filter: "agSetColumnFilter",
        valueFormatter: (params) => {
          const raw = params.value;
          if (!raw) return "";
          const d = new Date(raw);
          if (isNaN(d)) return raw;
          // console.log("raw  :", d.toISOString().split("T")[0]);
          return d.toISOString().split("T")[0];
        },
        filterParams: {
          values: getAsyncFilterValues,
          keyCreator: (p) => toYMD(p.value),
          valueFormatter: (p) => toYMD(p.value),
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
          treeList: true,
          treeListPathGetter: (rawDateValue) => {
            // console.log("rawDateValue :", rawDateValue);
            if (!rawDateValue) return ["Unknown"];
            const d = new Date(rawDateValue);
            if (isNaN(d)) return [rawDateValue];
            // console.log("d  :", d?.toLocaleDateString("fr-CA"));
            const year = String(d.getFullYear());
            const monthName = d.toLocaleString("en-US", { month: "short" });
            const day = String(d.getDate()).padStart(2, "0");
            // return d?.toLocaleDateString("fr-CA");
            return [year, monthName, day];
          },
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "name_and_deg_of_client",
        headerName: "Name and designation of the client",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "name_of_desig_of_the_client",
        headerName:
          "Name and designation of any relevant person acting on behalf of the client",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trader_id",
        headerName:
          "A designation to identify the trader (Trader ID) responsible within the investment firm for the investment decision",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "algorithm_id",
        headerName:
          "A designation to identify the Algo (Algo ID) responsible within the investment firm for the investment decision",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "txnr_ref_number",
        headerName: "Transaction reference number",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_id",
        headerName: "A designation to identify the order (Order ID)",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "idf_code_of_ord",
        headerName:
          "The identification code of the order assigned by the trading venue upon receipt of the order",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "unq_id_of_each_aggr_clients",
        headerName:
          "A unique identification for each group of aggregated clients' orders (which will be subsequently placed as one block order on a given trading venue).This identification should indicated ‘aggregated_X’ with X representing the number of clients whose orders have been aggregated",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "mic_code",
        headerName:
          "The segment MIC code of the trading venue to which the order has been submitted",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "name_of_odr_transmitted",
        headerName:
          "The name and other designation of the person to whom the order was transmitted",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "desg_of_seller_buyer",
        headerName: "Designation to identify the Seller & the Buyer",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trading_capacity",
        headerName: "The trading capacity",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "execution_trader_id",
        headerName:
          "A designation to identify the Trader (Trader ID) responsible for the execution",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "execution_algo_id",
        headerName:
          "A designation to identify the Algo (Algo ID) responsible for the execution",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "bs_indicator",
        headerName: "B/S indicator",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "date_and_exact_time_of_recipt",
        headerName:
          "The date and exact time of the receipt of the order or the dathe and exact time of when the decision to deal was made.  The exact time must be measured according to the methodology prescribed under the standards on clock synchronisation under Article 50(2) of Directive 2014/65/EU",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "ultimate_underlying",
        headerName: "Ultimate Underlying",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "put_call_indicator",
        headerName: "Put/Call identifier",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "strike_price",
        headerName: "Strike price",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "up_front_paymet",
        headerName: "Up-front payment",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "delivery_type",
        headerName: "Delivery type",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "option_style",
        headerName: "Option style",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "maturity_date",
        headerName: "Maturity date",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "unit_price_and_price_notation",
        headerName: "Unit price and price notation",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "price",
        headerName: "Price",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "price_multiplyer",
        headerName: "Price Multiplier",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "currency1",
        headerName: "Currency 1",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "currency2",
        headerName: "Currency 2",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "remaining_quantity",
        headerName: "Remaining Quantity",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "modified_quantity",
        headerName: "Modified Quantity",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "executed_quantity",
        headerName: "Executed Quantity",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "order_submission_datetime",
        headerName:
          "The date and exact time of submission of the order or decision to deal. The exact time must be measured according to the methodology prescribed under the standards on clock synchronisation under Article 50(2) of Directive 2014/65/EU",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "trading_venue_affecting_datetime",
        headerName:
          "The date and exact time of any message that is transmitted to and received from the trading venue in relation to any events affecting an order",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "another_invst_firm_affecting_datetime",
        headerName:
          "The date and exact time any message that is transmitted to and received from another investment firm in relation to any events affecting an order  The exact time must be measured according to the methodology prescribed under Commission Delegathed Regulation (EU) 2017/574 (1).  The exact time must be measured according to the methodology prescribed under the standards on clock synchronisation under Article 50(2) of Directive 2014/65/EU",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "message_from_trading_venue",
        headerName:
          "Any message that is transmitted to and received from the trading venue in relation to orders placed by the investment firm",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "details_recived_from_anothr_invst_firm",
        headerName:
          "Any other details and conditions that was submitted to and received from another investment firm in relation with the order",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "placed_order_sequence",
        headerName:
          "Each placed order's sequences in order to reflect the chronology of every event affecting it, including but not limited to modifications, cancellations and execution",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "short_selling_flag",
        headerName: "Short selling flag",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "ssr_ecemption_flag",
        headerName: "SSR exemption flag",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      {
        field: "waiver_flag",
        headerName: "Waiver flag",
        filter: "agTextColumnFilter",
        filterParams: {
          buttons: ["apply", "clear", "cancel"],
          maxNumConditions: 1,
          closeOnApply: true,
        },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      // Added Jan 23
      {
        field: "input_file_column",
        headerName: "Input File Column",
        filter: "agTextColumnFilter",
        filterParams: { maxNumConditions: 1 },
        cellStyle: { textAlign: "left" },
        resizable: true,
      },
      // ---
    ],
    [clientDetails, productCode, regulationCode, gridApi],
  );

  const activeColDefs = useMemo(() => {
    if (activeTab === "err") return errorColDefs;
    return recordsColDefs;
  }, [activeTab]);
  // console.log("columnDefs :", columnDefs.length);

  const onGridReady = useCallback(
    (params) => {
      setIsLoading(true);
      setGridApi(params.api);
      params.api.closeToolPanel();
      // console.log(gridApi);
      const datasource = {
        getRows: async (params) => {
          // console.log(clientDetails);
          // console.log(params);
          const {
            startRow,
            endRow,
            filterModel,
            sortModel,
            pivotMode,
            pivotCols, // ← AG Grid’s list of pivot columns
            valueCols, // ← AG Grid’s list of value columns
            rowGroupCols,
          } = params.request;

          // const limit = params.api.paginationGetPageSize();
          // const page = Math.floor(startRow / limit) + 1;

          // Process filters into the format your API expects.
          if (!startDate || !endDate) {
            // If either date is missing, skip the check
            return;
          }

          // const diffTime = Math.abs(startDate - endDate);
          // const diffDays = diffTime / (1000 * 60 * 60 * 24);
          // console.log("diffDays :", diffDays);
          // if (diffDays > 7) {
          //   toast.error("Maximum 7 days allowed");
          //   params.fail(); // Stop AG Grid request
          //   return;
          // }
          const s = startDate?.toLocaleDateString("fr-CA") ?? "";
          const e = endDate ? new Date(new Date(endDate).setDate(endDate.getDate() + 1)).toLocaleDateString("fr-CA") : "";
          // console.log("s and e :", s, e);
          const filters = {};
          Object.keys(filterModel).forEach((colId) => {
            const filterObj = filterModel[colId];
            if (filterObj.values) {
              filters[colId] = {
                type: filterObj.filterType,
                values: filterObj.values,
              };
            } else if (filterObj.conditions) {
              filters[colId] = {
                operator: filterObj.operator,
                conditions: filterObj.conditions.map((cond) => ({
                  type: cond.type,
                  value: cond.filter,
                })),
              };
            } else {
              filters[colId] = {
                type: filterObj.type,
                value: filterObj.filter,
              };
            }
          });
          let sort_by = "";
          let order = "";
          if (sortModel && sortModel.length > 0) {
            sort_by = sortModel[0].colId;
            order = sortModel[0].sort === "asc" ? "ascending" : "descending";
          }

          const queryParams = new URLSearchParams({
            startRow,
            endRow,
            client_id: clientDetails.clientId,
            product: productCode,
            regulation: regulationCode,
            filters: JSON.stringify(filters),
            fType: activeTab,
            ...(s && { start_date: s }),
            ...(e && { end_date: e }),
            email: clientDetails.email,
            datefiltercolumn: "order_date_regx",
            f_type: activeTabRef.current, // err || records
          });

          // Append sort parameters if sorting is applied.
          if (sort_by) {
            queryParams.append("sort_by", sort_by);
            queryParams.append("order", order);
          }
          // for (const [key, value] of queryParams.entries()) {
          //   console.log(`${key}: ${value}`);
          // }

          queryParams.append("pivotMode", pivotMode);

          const pivotFields = pivotCols.map((p) => p.field).join(","); // Columns
          const groupFields = rowGroupCols.map((r) => r.field).join(","); // Rows
          const aggField = valueCols[0]?.field || "";
          const aggFunc = valueCols[0]?.aggFunc || "";

          queryParams.append("pivotCols", pivotFields); // can be empty
          queryParams.append("rowgroupCols", groupFields); // can be empty
          queryParams.append("aggCol", aggField); // can be empty
          queryParams.append("aggFunc", aggFunc); // can be empty

          //     for (const [key, value] of queryParams.entries()) {
          //   console.log(`${key}: ${value}`);
          // }
          const regDataGridData = handleAPIEncryptValue(queryParams.toString());
          // console.log(regDataGridData);
          try {
            const gridUrl = getUrl("get-flask-data");
            const token = clientDetails.IdToken;
            const response = await fetch(
              `${gridUrl}?regdata=${regDataGridData}`,
              {
                method: "GET",
                mode: "cors",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                  "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
                },
              },
            );
            // console.log(response, "--response");
            const statusCode = response.status;
            // console.log("response :", response);
            // console.log("status :", response.status);
            if (statusCode === 403) {
              toast.error("Session expired. Please login again.");
              tokenExpire(statusCode, navigate);
              return;
            }
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();

            if (data && data.rowData) {
              setTotalRowCount(data.rowCount);

              const min = 1;
              const max = data.rowCount > 500000 ? data.rowCount : 500000;

              setRowRangeLimits({ min, max });
              setRowRange([min, max]);
              // console.log("data.rowData :", data.rowData);
              if (data.rowData.length === 0) {
                params.success({
                  rowCount: 0,
                  rowData: [],
                });
                params.api.showNoRowsOverlay();
              } else {
                params.api.hideOverlay();
                params.success({
                  rowData: data.rowData,
                  rowCount: data.rowCount,
                  ...(pivotMode && data.pivotFields?.length
                    ? { pivotResultFields: data.pivotFields }
                    : {}),
                });
              }
            } else {
              params.fail();
            }
          } catch (error) {
            console.error("Error fetching data:", error);
            params.fail();
          } finally {
            setIsLoading(false);
          }
        },
      };
      params.api.setGridOption("serverSideDatasource", datasource);
      setTimeout(() => {
        params.api.autoSizeAllColumns();
      }, 200);
    },

    [clientDetails, productCode, regulationCode, startDate, endDate, activeTab],
  );

  // --------------------------------------------------------------------------
  // 4. onFilterChanged: Refresh asynchronous filter values for the columns using set filters
  // --------------------------------------------------------------------------
  const onFilterChanged = useCallback(() => {
    if (filterChangedTimeout.current) {
      clearTimeout(filterChangedTimeout.current);
    }
    filterChangedTimeout.current = setTimeout(() => {
      if (!gridApi) return;
      const currentModel = gridApi.getFilterModel();
      const colDefs = gridApi.getColumnDefs() || [];
      colDefs.forEach((colDef) => {
        if (colDef.filter === "agTextColumnFilter") {
          // Determine if the filter for this column is active.
          const isActive = !!currentModel[colDef.field];
          // Only refresh if the column was inactive previously and remains inactive.
          if (!isActive && !prevAsyncActiveState.current[colDef.field]) {
            gridApi
              .getColumnFilterInstance(colDef.field)
              .then((filterInstance) => {
                if (
                  filterInstance &&
                  typeof filterInstance.refreshFilterValues === "function"
                ) {
                  try {
                    filterInstance.refreshFilterValues();
                  } catch (e) {
                    console.warn(
                      `Refresh error for ${colDef.field}: ${e.message}`,
                    );
                  }
                }
              });
          }
          // Update the previous active state.
          prevAsyncActiveState.current[colDef.field] = isActive;
        }
      });
    }, 300);
  }, [gridApi]);

  // --------------------------------------------------------------------------
  // 5. CSV export – downloads the CSV based on the current filter model.
  // --------------------------------------------------------------------------
  const downloadCSV = async () => {
    if (!gridApi) {
      console.error("gridApi is not available");
      return;
    }

    const MAX_ROWS = 500000;
    // console.log(totalRowCount, '=TotalRC');

    if (totalRowCount > MAX_ROWS) {
      setShowContactSupport(true);
      setShowSupportDialog(true); // 👈 open the dialog instead of alert
      return;
    }

    setIsDownloading(true);
    const selectedColumns = gridApi
      .getAllDisplayedColumns()
      .map((col) => col.getColId());
    const filterModel = gridApi.getFilterModel();
    const token = clientDetails.IdToken;
    const s = startDate?.toLocaleDateString("fr-CA") ?? "";
    const e = endDate ? new Date(new Date(endDate).setDate(endDate.getDate() + 1)).toLocaleDateString("fr-CA") : "";
    const queryParams = new URLSearchParams({
      client_id: clientDetails.clientId,
      product: productCode,
      regulation: regulationCode,
      email: clientDetails.email,
      ...(s && { start_date: s }),
      ...(e && { end_date: e }),
      datefiltercolumn: "order_date_regx",
      f_type: activeTabRef.current, // err || records
    });
    // for (const [key, value] of queryParams.entries()) {
    //   console.log(`${key}: ${value}`);
    // }
    queryParams.append("filters", JSON.stringify(filterModel));
    queryParams.append("columns", JSON.stringify(selectedColumns));
    // console.log("selectedColumns :", selectedColumns);
    // console.log("filterModel :", filterModel);
    const regDataGridData = handleAPIEncryptValue(queryParams.toString());

    try {
      const response = await fetch(
        `${downloadUrl}?regdata=${regDataGridData}`,
        {
          method: "GET",
          mode: "cors",
          headers: {
            "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const blob = await response.blob();
      const now = new Date();
      const pad = (num) => String(num).padStart(2, "0");
      const fileName = `annex_executions_${now.getFullYear()}${pad(
        now.getMonth() + 1,
      )}${pad(now.getDate())}_${pad(now.getHours())}${pad(
        now.getMinutes(),
      )}${pad(now.getSeconds())}.csv`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleContactSupport = async () => {
    if (!gridApi) return;

    const filterModel = gridApi.getFilterModel();
    const selectedColumns = gridApi
      .getAllDisplayedColumns()
      .map((col) => col.getColId());

    const payload = {
      client_id: clientDetails.clientId,
      product: productCode,
      regulation: regulationCode,
      email: clientDetails.email,
      filters: filterModel,
      columns: selectedColumns,
      rowCount: totalRowCount,
      requestedRange: { min: rowRange[0], max: rowRange[1] },
      timestamp: new Date().toISOString(),
    };

    const encryptedPayload = handleAPIEncryptValue(JSON.stringify(payload));
    const supportUrl = getUrl("contact-support"); // 👈 Your API key name here

    try {
      const response = await fetch(`${supportUrl}`, {
        method: "POST",
        headers: {
          "x-api-key": clientDetails.REGDATA_FLASK_APP_ENCRYPTION_KEY,
          Authorization: `Bearer ${clientDetails.IdToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ regdata: payload }),
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.statusText}`);
      }

      alert("Support request sent successfully!");
      setShowContactSupport(false); // Optional: hide the support message again
    } catch (error) {
      console.error("Failed to send support request:", error);
      alert(
        "Something went wrong while sending the request. Please try again later.",
      );
    }
  };

  // --------------------------------------------------------------------------
  // 6. Check regulation accessibility based on client details.
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (gridApi && isActive) {
      gridApi.autoSizeAllColumns();
    }
  }, [gridApi, isActive]);

  const onFirstDataRendered = useCallback(
    (params) => {
      if (!isActive) return;
      params.api.autoSizeAllColumns();
    },
    [isActive],
  );

  const onModelUpdated = useCallback(
    (params) => {
      if (!isActive) return;
      params.api.autoSizeAllColumns();
    },
    [isActive],
  );
  // Fetch KPI data when component mounts or dates change
  useEffect(() => {
    if (isRegulationAccessible && startDate && endDate && isActive) {
      fetchKpiData();
    }
  }, [startDate, endDate, isRegulationAccessible, isActive]);

  useEffect(() => {
    if (
      productCode in clientDetails.dataAccess &&
      clientDetails.dataAccess.P001?.includes(regulationCode)
    ) {
      setIsRegulationAccessible(true);
    } else {
      setIsRegulationAccessible(false);
    }
  }, [regulationCode, clientDetails, productCode]);

  return (
    <main
      className={`${isFullscreen ? "" : "middle_section"} dashboard_middle`}
    >
      <div className="container-fluid">
        {/* KPIs Section */}

        {isRegulationAccessible ? (
          <>
            <div className="grid-kpi-wrapper">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="col-auto px-0">
                  <CustomDatepicker
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(newDateRange) => {
                      setDateRange(newDateRange);
                      if (newDateRange[0] && newDateRange[1]) {
                        setTimeout(() => fetchKpiData(), 100);
                      }
                    }}
                    showWrapperStyles={false}
                  />
                </div>
                <GridGroupIcons
                  onRefresh={() => {
                    handleRefresh();
                    fetchKpiData();
                  }}
                  fullScreenIcon={false}
                  iconsDisabled={isLoading || kpiLoading}
                  downloadIcon={false}
                  // borderRadius={10}
                />
              </div>
              <div className="kpis">
                {/* TOTAL */}
                <div className="kpi violet">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Total Records</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.totalExe)}</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    in current view
                    <span onClick={() => openBasicKpiModal("totalRecords")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* VALID */}
                <div className="kpi green">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Processed Records</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.validExe)}</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    {kpiData.validPctOfTotal.toFixed(2)}% of total
                    <span onClick={() => openBasicKpiModal("processedRecords")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* INVALID */}
                <div className="kpi red">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Exceptions</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.invalidExe)}</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    {kpiData.invalidPctOfTotal.toFixed(2)}% of total
                    <span
                      onClick={() => openBasicKpiModal("exceptionsRecords")}
                    >
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* EXCLUDED */}
                <div className="kpi amber">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Excluded Records</div>
                      <div className="kpi-value">
                        <KpiValue>{fmt(kpiData.excludedExe)}</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    {kpiData.excludedPctOfTotal.toFixed(2)}% of total
                    <span onClick={() => openBasicKpiModal("excludedRecords")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* ACCURACY */}
                <div className="kpi green">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Accuracy %</div>
                      <div className="kpi-value">
                        <KpiValue>{kpiData.accuracyPct.toFixed(2)}%</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    <span
                      onClick={() => openBasicKpiModal("accuracyPercentage")}
                    >
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>

                {/* ERROR */}
                <div className="kpi red">
                  <div className="kpi-single-value">
                    <div>
                      <div className="kpi-title">Error %</div>
                      <div className="kpi-value">
                        <KpiValue>{kpiData.errorPct.toFixed(2)}%</KpiValue>
                      </div>
                    </div>
                  </div>
                  <div className="kpi-foot">
                    <span onClick={() => openBasicKpiModal("errorPercentage")}>
                      <FaInfoCircle className="kpi-info-icon" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`dashboard_inner pt-0 ${
                isFullscreen ? "fullscreen-grid" : "mt-3"
              }`}
            >
              <div
                className={`rx_table_wrapper table-responsive padding-fix ${
                  activeTab == "err" ? "red-grid" : ""
                }`}
              >
                <div className="d-flex justify-content-between align-items-center grid-icon-wrapper">
                  <DynamicTabSwitcher
                    onTabChange={handleTabChange}
                    tabMap={TAB_MAP}
                    activeTab={activeTab}
                    wrapperClassName="pt-0 pb-0"
                    disabledTabs={[]}
                  />
                  <GridGroupIcons
                    refreshIcon={false}
                    isFullscreen={isFullscreen}
                    iconsDisabled={isLoading || kpiLoading}
                    onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
                    onDownloadCSV={downloadCSV}
                    isDownloading={isDownloading}
                    // borderRadius={10}
                  />
                </div>
                <div className={`container-fluid p-0`}>
                  <div
                    className="ag-theme-alpine ag-theme-quartz"
                    style={{
                      height: isFullscreen ? "calc(100vh - 60px)" : "80vh",
                      width: "100%",
                    }}
                  >
                    <AgGridReact
                      ref={gridRef}
                      key={gridKey}
                      theme="legacy"
                      columnDefs={activeColDefs}
                      defaultColDef={defaultColDef}
                      rowModelType="serverSide"
                      pagination={true}
                      paginationPageSize={20}
                      paginationPageSizeSelector={[10, 20, 50, 100]}
                      cacheBlockSize={10}
                      onFirstDataRendered={onFirstDataRendered}
                      onModelUpdated={onModelUpdated}
                      onGridReady={onGridReady}
                      onFilterChanged={onFilterChanged}
                      onCellMouseOver={onCellMouseOver}
                      onCellMouseOut={onCellMouseOut}
                      onRowDoubleClicked={handleDoubleClick}
                      cellSelection={true}
                      enableCharts
                      // pivotMode={false}
                      // pivotPanelShow="always"
                      // sideBar="columns"
                      // serverSidePivotResultFieldSeparator="."
                      noRowsOverlayComponent={GridDataNotFound}
                    />
                  </div>
                </div>
              </div>
              {/* <div className="d-flex justify-content-end mt-4">
              <button
                className="dash_btn"
                type="submit"
                onClick={downloadCSV}
                disabled={isDownloading}
                style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
              >
                {isDownloading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Download CSV"}
              </button>
            </div> */}
              {/* {showContactSupport && (
              <div className="d-flex flex-column align-items-start mt-3">
                <p style={{ color: "red", fontSize: "0.9em" }}>
                  You've exceeded the 500000 row download limit. Please contact support for full data.
                </p>
                <button
                  className="dash_btn"
                  type="button"
                  onClick={handleContactSupport}
                  style={{ marginTop: "4px" }}
                >
                  Contact Support
                </button>
              </div>
            )} */}
            </div>
          </>
        ) : (
          <NotSubscribedMessageComponent />
        )}
      </div>
      <GridRowDetailsExpand
        showDialog={showDialog}
        selectedRow={selectedRow}
        hoverPos={hoverPos}
        isErrGrid={activeTab === "err"}
        isDetailBoxOpen={isDetailBoxOpen}
        setIsDetailBoxOpen={setIsDetailBoxOpen}
      />

      {/* ✅ Place your dialog here */}
      <Dialog
        sx={{ zIndex: 99999999 }}
        open={showSupportDialog}
        disableScrollLock
        onClose={() => setShowSupportDialog(false)}
        fullWidth
        maxWidth="md"
      >
        {showTable ? (
          <>
            <DialogTitle>Requested Files</DialogTitle>
            <DialogContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Requested On</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                      { name: "rts24_jan_data.csv", date: "2025-01-10" },
                      { name: "rts24_feb_data.csv", date: "2025-02-14" },
                      { name: "rts24_mar_data.csv", date: "2025-03-05" },
                    ].map((file, index) => (
                      <TableRow key={index}>
                        <TableCell>{file.name}</TableCell>
                        <TableCell>{file.date}</TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() =>
                              toast.info(`Downloading ${file.name}...`)
                            }
                          >
                            Download
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowTable(false)} color="primary">
                Back
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle fontSize={22} fontWeight={500}>
              Download Limit Exceeded
            </DialogTitle>
            <DialogContent>
              <Typography mb={2} fontSize={16}>
                Download limit is 5,00,000 rows. Please contact support for data
                more than 5,00,000 rows.
              </Typography>
              <Typography mb={0} fontSize={14}>
                Contact Us:{" "}
                <p style={{ color: "blue", display: "inline" }}>
                  {" "}
                  support.reg-x.co.uk{" "}
                </p>
              </Typography>
              {/* <Box sx={{ px: 2, pt: 1 }}>
                <Slider
                  value={rowRange}
                  onChange={(e, newValue) => setRowRange(newValue)}
                  valueLabelDisplay="auto"
                  min={rowRangeLimits.min}
                  max={rowRangeLimits.max}
                  step={10000}
                  marks={[
                    { value: rowRangeLimits.min, label: `${rowRangeLimits.min}` },
                    { value: rowRangeLimits.max, label: `${rowRangeLimits.max}` },
                  ]}
                />

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <TextField
                    label="Min Rows"
                    type="number"
                    value={rowRange[0]}
                    onChange={(e) => {
                      const newMin = parseInt(e.target.value, 10);
                      if (!isNaN(newMin)) {
                        // Ensure min doesn't exceed max
                        setRowRange(([_, max]) => [Math.min(newMin, max), max]);
                      }
                    }}
                    inputProps={{ min: rowRangeLimits.min, max: rowRangeLimits.max }}
                    fullWidth
                  />
                  <TextField
                    label="Max Rows"
                    type="number"
                    value={rowRange[1]}
                    onChange={(e) => {
                      const newMax = parseInt(e.target.value, 10);
                      if (!isNaN(newMax)) {
                        // Ensure max doesn't fall below min
                        setRowRange(([min, _]) => [min, Math.max(newMax, min)]);
                      }
                    }}
                    inputProps={{ min: rowRangeLimits.min, max: rowRangeLimits.max }}
                    fullWidth
                  />
                </Box>

                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected Range: {rowRange[0].toLocaleString()} - {rowRange[1].toLocaleString()} rows
                </Typography>
              </Box> */}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowSupportDialog(false);
                }}
                color="primary"
              >
                Cancel
              </Button>
              {/* <Button
                onClick={() => {
                  handleContactSupport();
                  setShowSupportDialog(false);
                }}
                color="primary"
                variant="contained"
              >
                SEND REQUEST
              </Button>
              <Button
                onClick={() => setShowTable(true)}
                color="secondary"
                variant="outlined"
              >
                Show Requested Files
              </Button> */}
            </DialogActions>
          </>
        )}
      </Dialog>
      <BasicKPICalculation
        kpiKey={activeBasicKpiKey}
        regulationCode={regulationCode}
        kpiData={kpiData}
        onClose={closeBasicKpiModal}
      />
    </main>
  );
}

export default UKRecordKeepingAnnexIVExecutionsComponent;
